import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { SlotParticipantStatus } from "@/modules/profile/subscriber/gateway/subscriberDtos"
import { ProgramInfiniteSessionTypeOf, ProgramOnOffSessionTypeOf } from "@/modules/sessions/programSessionTypes"
import { shortenText } from "@/modules/utils/stringUtils"
import { BoxSlotTypeOf } from "@modules/boxSlot/boxSlotTypes"
import { type EventType } from "@modules/calendar/calendarTypes"
import { word } from "@modules/core/utils/i18n"
import { ProfileAvatar } from "@modules/profile/common/components/profileAvatar"
import { UserEntity } from "@modules/profile/userProfile/userEntity"
import { ProgramType, ProgramVisibility } from "@modules/programs/types/programTypes"
import { type DetailedSessionType } from "@modules/sessions/sessionsType"
import dayjs from "dayjs"
import { EyeOffIcon, MapPinIcon } from "lucide-react"
import { useMemo } from "react"

export type TrainingCalendarEventType = EventType & {
  sessionType: typeof BoxSlotTypeOf | typeof ProgramInfiniteSessionTypeOf | typeof ProgramOnOffSessionTypeOf
  author?: UserEntity | null
  boxName?: string
  participantCount?: number
  participantStatus?: SlotParticipantStatus
  participantLimit?: number
  programName: string
  programId: string
  isAuthor: boolean
  programVisibility?: ProgramVisibility
  hideSessionContent: boolean
}

type TrainingCalendarEventProps = {
  index: number
  event: TrainingCalendarEventType | undefined
  onEventClick: (arg: DetailedSessionType) => void
  isUpdating?: boolean
  isDetailsOpen?: boolean
} & React.ComponentProps<"div">

export function TrainingCalendarEvent({
  index,
  event,
  onEventClick,
  isDetailsOpen = false,
  className,
}: TrainingCalendarEventProps) {
  const sessionHasStarted = useMemo(
    () => event && "date" in event && event.date && new Date() > new Date(event.date),
    [event],
  )

  const hideSessionContent = useMemo(() => {
    if (sessionHasStarted) {
      return false
    }

    return event?.hideSessionContent
  }, [event, sessionHasStarted])

  if (!event) return <></>

  const {
    title,
    sessionType,
    boxName,
    programName,
    isAuthor,
    author,
    participantCount,
    participantLimit,
    participantStatus,
  } = event

  return (
    <div key={index} id={event.id} className={cn("border-b pb-2", className)}>
      <div
        onClick={() => {
          if (sessionType !== BoxSlotTypeOf) {
            onEventClick({
              id: event.id,
              programId: event.programId,
              programType: sessionType === ProgramOnOffSessionTypeOf ? ProgramType.ONOFF : ProgramType.VIRTUAL,
              date: "date" in event ? event.date : null,
            })
          }
        }}
        className={cn("flex flex-col gap-1 rounded-3xl p-2 transition-colors duration-200 hover:bg-primary-100", {
          "cursor-pointer": !!onEventClick,
          "bg-primary-100": isDetailsOpen,
        })}
      >
        <div className="mb-0 flex flex-row justify-between gap-2">
          <Text
            fontStyle="TS"
            className={cn("event-title rounded-xl px-2 py-1", {
              "text-primary bg-neutral-100": isAuthor,
              "bg-secondary-500 text-white": !isAuthor,
            })}
          >
            {shortenText(title ?? "", 60)}
          </Text>
          {sessionType === BoxSlotTypeOf && "date" in event ? (
            <Text fontStyle="BM" className="break-keep">
              {dayjs(event.date).format("H:mm")}
            </Text>
          ) : (
            hideSessionContent && <EyeOffIcon className="text-green-300" size={16} />
          )}
        </div>

        <Text fontStyle="BS" className="mt-0">
          {sessionType === BoxSlotTypeOf ? (
            <div className="flex flex-row">
              <MapPinIcon size={16} className="mr-1 text-green-300" />
              {shortenText(boxName ?? "", 60)}
            </div>
          ) : (
            shortenText(programName ?? "", 60)
          )}
        </Text>

        {author && (
          <div className="mt-2 flex flex-row items-center">
            <ProfileAvatar profile={author} />
            <div className="ml-2 flex flex-col">
              <Text fontStyle="LM">{`${author.firstname} ${author.lastname}`}</Text>
              <Text fontStyle="BS">{word("program.label.author")}</Text>
            </div>
          </div>
        )}

        {sessionType === BoxSlotTypeOf && (
          <div className="mb-2 flex flex-row items-center">
            <Text fontStyle="BS" className="shrink-0 rounded-xl bg-neutral-100 px-2 py-1">
              {participantLimit
                ? `${participantCount}/${participantLimit} ${word("slot.participants", { count: participantCount })}`
                : `${participantCount} ${word("slot.participants", { count: participantCount })}`}
            </Text>

            {participantStatus === SlotParticipantStatus.REGISTERED && (
              <Text
                fontStyle="LM"
                className="shrink-0 rotate-350 rounded-xl bg-secondary-500 px-2 py-1 text-textOnColor-body"
              >
                {word("programs.going")}
              </Text>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
